import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';
import { ApiService } from './api.service';
import { SettingsService } from '../settings/settings.service';
import { UserPermissionsResponse, ModuleTree, DesignationPermissionMatrix } from '../common/models/permission.model';
import { isAdminUser } from '../utils';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private _permissionsSubject: BehaviorSubject<UserPermissionsResponse | null> =
    new BehaviorSubject<UserPermissionsResponse | null>(null);
  public permissions$: Observable<UserPermissionsResponse | null> = this._permissionsSubject.asObservable();

  private _baseUrl = 'permissions';

  constructor(
    private _apiService: ApiService,
    private _settingsService: SettingsService
  ) {}

  /**
   * Fetch permissions for the logged-in user
   */
  fetchMyPermissions(): Observable<UserPermissionsResponse> {
    return this._apiService.get<UserPermissionsResponse>(`${this._baseUrl}/user-permissions/me/`).pipe(
      tap(res => {
        this._permissionsSubject.next(res);
      })
    );
  }

  /**
   * True for Django staff and for users carrying the admin role. Endpoints gated on
   * IsAdminUser answer 403 for everyone else, so use this to hide those entry points
   * rather than a permission code, which the designation matrix could grant to a user
   * who would still get a 403.
   */
  isAdminUser(): boolean {
    return isAdminUser(this._settingsService.getCurrentUser());
  }

  /**
   * Check if the current user has a specific permission code
   * @param code format: 'module.resource.action'
   */
  hasPermission(code: string): boolean {
    const user = this._settingsService.getCurrentUser();
    // Admins (staff) automatically have all permissions
    if (user?.is_staff) {
      return true;
    }

    const permissions = this._permissionsSubject.value?.permissions || [];
    return permissions.includes(code);
  }

  /**
   * Check if user has any of the provided permission codes
   */
  hasAnyPermission(codes: string[]): boolean {
    const user = this._settingsService.getCurrentUser();
    if (user?.is_staff) {
      return true;
    }

    const permissions = this._permissionsSubject.value?.permissions || [];
    return codes.some(code => permissions.includes(code));
  }

  /**
   * Check if user has access to a module (has any permission in that module)
   */
  hasModuleAccess(moduleName: string): boolean {
    const user = this._settingsService.getCurrentUser();
    if (user?.is_staff) {
      return true;
    }

    const modules = this._permissionsSubject.value?.modules || {};
    return !!modules[moduleName];
  }

  /**
   * Get all system permissions grouped by module/resource (for Admin UI)
   */
  getPermissionTree(): Observable<ModuleTree[]> {
    return this._apiService.get<ModuleTree[]>(`${this._baseUrl}/permissions/modules/`);
  }

  /**
   * Get the permission matrix for a specific designation (for Admin UI)
   */
  getDesignationMatrix(designationId: string): Observable<DesignationPermissionMatrix> {
    return this._apiService.get<DesignationPermissionMatrix>(
      `${this._baseUrl}/designation-permissions/matrix/${designationId}/`
    );
  }

  /**
   * Update permissions for a designation (for Admin UI)
   */
  updateDesignationPermissions(designationId: string, permissionIds: string[]): Observable<any> {
    return this._apiService.put(`${this._baseUrl}/designation-permissions/bulk-assign/`, {
      designation_id: designationId,
      permission_ids: permissionIds,
    });
  }

  /**
   * Clear permissions on logout
   */
  clear(): void {
    this._permissionsSubject.next(null);
  }
}
